<template>
  <div class="min-h-screen bg-[#fcfcfc] text-[#1a1a1a] selection:bg-indigo-100 selection:text-indigo-900 overflow-x-hidden font-['Inter']">
    <!-- Waitlist Modal -->
    <div v-if="isModalOpen" class="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-300 overflow-y-auto">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-md" @click="closeModal"></div>
      <div class="relative bg-white w-full max-w-xl rounded-[24px] sm:rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 my-auto">
        <button @click="closeModal" class="absolute top-4 right-4 sm:top-8 sm:right-8 p-2 hover:bg-gray-100 rounded-2xl transition-all text-gray-400 z-10">
          <X class="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        <div class="p-6 sm:p-8 lg:p-12 space-y-6 sm:space-y-8 lg:space-y-10">
          <div v-if="submitted" class="text-center space-y-4 sm:space-y-6 py-6 sm:py-10 animate-in fade-in zoom-in-95 duration-500">
            <div class="w-16 h-16 sm:w-20 sm:h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto border border-green-100">
              <CheckCircle2 class="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />
            </div>
            <div class="space-y-2">
                <h3 class="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">{{ $t('uTable.modal.successTitle') }}</h3>
              <p class="text-sm sm:text-base text-gray-500 font-medium leading-relaxed px-2">{{ $t('uTable.modal.successMessage') }}</p>
            </div>
            <button @click="closeModal" class="px-8 sm:px-10 py-3 sm:py-4 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl transition-all hover:bg-black">{{ $t('uTable.modal.close') }}</button>
          </div>
          <template v-else>
            <div class="space-y-3 sm:space-y-4">
              <div class="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 border border-indigo-100 text-indigo-600 rounded-full text-[8px] sm:text-[9px] font-black uppercase tracking-[0.2em]">
                <Lock class="w-2.5 h-2.5 sm:w-3 sm:h-3" /> {{ $t('uTable.modal.badge') }}
              </div>
              <h2 class="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 tracking-tighter leading-none">
                {{ $t('uTable.modal.title') }} <br/><span class="text-indigo-600">{{ $t('uTable.modal.titleHighlight') }}</span>
              </h2>
              <p class="text-sm sm:text-base text-gray-500 font-medium leading-relaxed">
                {{ $t('uTable.modal.description') }}
              </p>
            </div>
            <form @submit.prevent="handleModalSubmit" class="space-y-4 sm:space-y-6">
              <div class="space-y-3 sm:space-y-4">
                <div class="relative">
                  <Mail class="absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                  <input 
                    type="email" 
                    required
                    :placeholder="$t('uTable.modal.emailPlaceholder')" 
                    class="w-full pl-11 sm:pl-14 pr-4 py-4 sm:py-5 bg-white border border-gray-200 rounded-2xl sm:rounded-3xl text-sm font-bold focus:ring-[8px] sm:focus:ring-[12px] focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all"
                    v-model="modalEmail"
                    :disabled="isSubmitting"
                  />
                </div>
                <div class="relative">
                  <Settings2 class="absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                  <input 
                    type="text" 
                    :placeholder="$t('uTable.modal.businessPlaceholder')" 
                    class="w-full pl-11 sm:pl-14 pr-4 py-4 sm:py-5 bg-white border border-gray-200 rounded-2xl sm:rounded-3xl text-sm font-bold focus:ring-[8px] sm:focus:ring-[12px] focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all"
                    v-model="businessName"
                    :disabled="isSubmitting"
                  />
                </div>
              </div>
              <button 
                type="submit"
                :disabled="isSubmitting"
                class="w-full py-4 sm:py-5 bg-[#DFFF1A] text-black border border-[#DFFF1A] rounded-2xl sm:rounded-3xl font-black text-xs uppercase tracking-widest shadow-2xl shadow-lime-200/50 hover:bg-lime-400 transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="!isSubmitting">{{ $t('uTable.modal.submit') }}</span>
                <span v-else>{{ $t('uTable.modal.submitting') }}</span>
                <ArrowRight v-if="!isSubmitting" class="w-4 h-4" />
              </button>
              <p class="text-[9px] sm:text-[10px] text-gray-300 font-bold uppercase tracking-widest text-center px-2">{{ $t('uTable.modal.disclaimer') }}</p>
            </form>
          </template>
        </div>
      </div>
    </div>

    <!-- Background Decor -->
    <div 
      class="fixed inset-0 z-0 pointer-events-none opacity-[0.05]" 
      :style="{ backgroundImage: 'radial-gradient(#4f46e5 1px, transparent 0)', backgroundSize: '40px 40px' }"
    ></div>
    <div class="fixed inset-0 z-0 pointer-events-none bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:100px_100px]"></div>

    <!-- Nav -->
    <nav class="fixed top-0 left-0 right-0 h-20 bg-white/80 backdrop-blur-xl border-b border-gray-100 z-[100] px-4 sm:px-8">
      <div class="max-w-7xl mx-auto h-full flex items-center justify-between">
        <div class="flex items-center gap-2 sm:gap-3">
          <img :src="LOGO_URL" class="w-12 h-12 sm:w-14 sm:h-14 object-contain" alt="U-Table Logo" />
          <span class="text-xl sm:text-2xl font-black tracking-tighter">U-Table</span>
        </div>
        
        <!-- Desktop Menu -->
        <div class="hidden md:flex items-center gap-10">
          <button @click="openWaitlist" class="text-sm font-bold text-gray-400 hover:text-indigo-600 transition-colors">{{ $t('uTable.nav.features') }}</button>
          <button @click="openWaitlist" class="text-sm font-bold text-gray-400 hover:text-indigo-600 transition-colors">{{ $t('uTable.nav.freePlan') }}</button>
          <button @click="openWaitlist" class="text-sm font-bold text-gray-400 hover:text-indigo-600 transition-colors">{{ $t('uTable.nav.integration') }}</button>
        </div>
        
        <!-- Desktop CTA Buttons -->
        <div class="hidden md:flex items-center gap-4">
          <LanguageSwitcher />
          <button @click="openWaitlist" class="text-sm font-bold text-gray-500 hover:text-gray-900 px-4 py-2">{{ $t('uTable.nav.login') }}</button>
          <button 
            @click="openWaitlist"
            class="px-6 py-2.5 bg-indigo-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all"
          >
            {{ $t('uTable.nav.joinList') }}
          </button>
        </div>

        <!-- Mobile Menu Button -->
        <div class="md:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <button 
            @click="mobileMenuOpen = !mobileMenuOpen"
            class="p-2 text-gray-600 hover:text-indigo-600 transition-colors"
            aria-label="Menu"
          >
            <svg v-if="!mobileMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
            <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Menu -->
      <div 
        v-if="mobileMenuOpen"
        class="md:hidden absolute top-20 left-0 right-0 bg-white border-b border-gray-100 shadow-lg z-[99]"
      >
        <div class="px-4 py-4 space-y-4">
          <button @click="handleMobileWaitlist" class="block w-full text-left text-sm font-bold text-gray-400 hover:text-indigo-600 transition-colors py-2">{{ $t('uTable.nav.features') }}</button>
          <button @click="handleMobileWaitlist" class="block w-full text-left text-sm font-bold text-gray-400 hover:text-indigo-600 transition-colors py-2">{{ $t('uTable.nav.freePlan') }}</button>
          <button @click="handleMobileWaitlist" class="block w-full text-left text-sm font-bold text-gray-400 hover:text-indigo-600 transition-colors py-2">{{ $t('uTable.nav.integration') }}</button>
          <div class="pt-4 border-t border-gray-100 space-y-2">
            <div class="px-2 py-2">
              <LanguageSwitcher />
            </div>
            <button @click="handleMobileWaitlist" class="block w-full text-left text-sm font-bold text-gray-500 hover:text-gray-900 py-2">{{ $t('uTable.nav.login') }}</button>
            <button 
              @click="handleMobileWaitlist"
              class="w-full px-6 py-3 bg-indigo-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all"
            >
              {{ $t('uTable.nav.joinList') }}
            </button>
          </div>
        </div>
      </div>
    </nav>

    <!-- Hero Section -->
    <section class="relative pt-32 sm:pt-44 pb-20 sm:pb-32 px-4 sm:px-8 z-10">
      <div class="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-24 items-center">
        <div class="space-y-10 animate-in fade-in slide-in-from-left-10 duration-1000">
          <div class="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-50 border border-indigo-100 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-[0.2em]">
            <Sparkles class="w-3.5 h-3.5 fill-indigo-600" /> {{ $t('uTable.hero.badge') }}
          </div>
          
          <h1 class="text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tighter leading-[0.9] text-gray-900">
            {{ $t('uTable.hero.title') }}<br/> <span class="text-indigo-600">{{ $t('uTable.hero.titleHighlight') }}</span>
          </h1>
          
          <p class="text-lg sm:text-xl text-gray-500 font-medium max-w-lg leading-relaxed">
            {{ $t('uTable.hero.description') }}
          </p>
          <form @submit.prevent="handleHeroSubmit" class="relative max-w-2xl group">
            <div class="flex flex-col sm:flex-row gap-3">
              <div class="flex-1 relative">
                <Mail class="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input 
                  type="email" 
                  required
                  :placeholder="$t('uTable.hero.emailPlaceholder')" 
                  class="w-full pl-14 pr-4 py-5 bg-white border border-gray-200 rounded-[24px] text-sm font-bold focus:ring-[12px] focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all shadow-sm"
                  v-model="heroEmail"
                />
              </div>
              <button 
                type="submit"
                class="px-8 py-5 bg-[#DFFF1A] text-black border border-[#DFFF1A] rounded-[24px] font-black text-[11px] uppercase tracking-widest shadow-2xl shadow-lime-200/50 hover:bg-lime-400 transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2 shrink-0"
              >
                {{ $t('uTable.hero.cta') }} <ArrowRight class="w-4 h-4" />
              </button>
            </div>
          </form>
          <div class="flex items-center gap-8 pt-6">
             <div class="flex -space-x-3">
               <div 
                 v-for="i in 4" 
                 :key="i" 
                 class="w-10 h-10 rounded-full border-4 border-[#fcfcfc] bg-gray-200 overflow-hidden"
               >
                 <img :src="`https://i.pravatar.cc/100?u=${i+20}`" alt="user" />
               </div>
             </div>
             <p class="text-xs font-bold text-gray-400">{{ $t('uTable.hero.socialProof') }}</p>
          </div>
        </div>
        <div class="relative animate-in fade-in zoom-in-95 duration-1000 delay-200 hidden lg:block">
          <div class="stripe-card relative z-10 border border-gray-100 overflow-hidden shadow-[0_40px_80px_-15px_rgba(0,0,0,0.1)] rounded-[40px] bg-white cursor-pointer" @click="openWaitlist">
            <div class="h-12 border-b border-gray-50 bg-gray-50/50 flex items-center px-6 gap-2">
               <div class="flex gap-1.5">
                 <div class="w-3 h-3 rounded-full bg-red-400/20"></div>
                 <div class="w-3 h-3 rounded-full bg-amber-400/20"></div>
                 <div class="w-3 h-3 rounded-full bg-green-400/20"></div>
               </div>
               <div class="mx-auto bg-white border border-gray-100 rounded-full px-4 py-1 text-[9px] font-black text-gray-300 uppercase tracking-widest">u-table.com/admin</div>
            </div>
            <div class="p-8 space-y-8">
               <div class="space-y-6">
                  <div class="p-6 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-3xl space-y-2">
                     <div class="flex items-center gap-3">
                        <div class="w-10 h-10 bg-indigo-600 rounded-xl shadow-sm flex items-center justify-center">
                           <Users class="w-5 h-5 text-white" />
                        </div>
                        <div>
                           <p class="text-[10px] font-black text-indigo-600 uppercase tracking-widest">{{ $t('uTable.hero.dashboardLabel') }}</p>
                           <p class="text-sm font-bold text-gray-700">{{ $t('uTable.hero.dashboardDesc') }}</p>
                        </div>
                     </div>
                  </div>
                  <div class="p-6 bg-gray-50 rounded-3xl space-y-2">
                     <div class="flex items-center gap-3">
                        <div class="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center">
                           <Smartphone class="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                           <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">{{ $t('uTable.hero.notificationsLabel') }}</p>
                           <p class="text-sm font-bold text-gray-700">{{ $t('uTable.hero.notificationsDesc') }}</p>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="py-40 px-4 sm:px-8 relative bg-gradient-to-b from-[#fcfcfc] to-white">
      <div class="max-w-7xl mx-auto space-y-20">
        <div class="text-center space-y-6 max-w-3xl mx-auto">
          <h2 class="text-[11px] font-black uppercase text-indigo-600 tracking-[0.3em]">{{ $t('uTable.howItWorks.title') }}</h2>
          <h3 class="text-4xl sm:text-5xl font-black tracking-tighter text-gray-900 leading-tight">{{ $t('uTable.howItWorks.subtitle') }}</h3>
          <p class="text-gray-500 font-medium text-lg">{{ $t('uTable.howItWorks.description') }}</p>
        </div>

        <div class="max-w-7xl mx-auto space-y-24">
          <!-- Step 1 -->
          <div class="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            <div class="flex-shrink-0 w-full lg:w-1/2 max-w-lg">
              <div class="relative">
                <div class="absolute -top-6 -left-6 w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center text-white font-black text-2xl z-10 shadow-2xl">
                  1
                </div>
                <div class="bg-white border-2 border-gray-100 rounded-[40px] overflow-hidden shadow-2xl p-6">
                  <img 
                    src="https://i.ibb.co/spzJPWQ1/Screenshot-2025-12-22-at-15-52-03.png" 
                    alt="Step 1: Scegli il servizio" 
                    class="w-full h-auto rounded-3xl"
                  />
                </div>
              </div>
            </div>
            <div class="flex-1 space-y-6 text-center lg:text-left lg:pl-8">
              <h4 class="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 leading-tight">{{ $t('uTable.howItWorks.step1.title') }}</h4>
              <p class="text-xl sm:text-2xl text-gray-500 font-medium leading-relaxed max-w-xl">{{ $t('uTable.howItWorks.step1.description') }}</p>
            </div>
          </div>

          <!-- Step 2 -->
          <div class="flex flex-col lg:flex-row-reverse items-center gap-12 lg:gap-20">
            <div class="flex-shrink-0 w-full lg:w-1/2 max-w-lg">
              <div class="relative">
                <div class="absolute -top-6 -left-6 w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center text-white font-black text-2xl z-10 shadow-2xl">
                  2
                </div>
                <div class="bg-white border-2 border-gray-100 rounded-[40px] overflow-hidden shadow-2xl p-6">
                  <img 
                    src="https://i.ibb.co/ZR6ppsQd/Screenshot-2025-12-22-at-15-52-11.png" 
                    alt="Step 2: Seleziona data e ora" 
                    class="w-full h-auto rounded-3xl"
                  />
                </div>
              </div>
            </div>
            <div class="flex-1 space-y-6 text-center lg:text-right lg:pr-8">
              <h4 class="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 leading-tight">{{ $t('uTable.howItWorks.step2.title') }}</h4>
              <p class="text-xl sm:text-2xl text-gray-500 font-medium leading-relaxed max-w-xl ml-auto">{{ $t('uTable.howItWorks.step2.description') }}</p>
            </div>
          </div>

          <!-- Step 3 -->
          <div class="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            <div class="flex-shrink-0 w-full lg:w-1/2 max-w-lg">
              <div class="relative">
                <div class="absolute -top-6 -left-6 w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center text-white font-black text-2xl z-10 shadow-2xl">
                  3
                </div>
                <div class="bg-white border-2 border-gray-100 rounded-[40px] overflow-hidden shadow-2xl p-6">
                  <img 
                    src="https://i.ibb.co/kdNknMR/Screenshot-2025-12-22-at-15-52-20.png" 
                    alt="Step 3: Dati per il follow-up" 
                    class="w-full h-auto rounded-3xl"
                  />
                </div>
              </div>
            </div>
            <div class="flex-1 space-y-6 text-center lg:text-left lg:pl-8">
              <h4 class="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 leading-tight">{{ $t('uTable.howItWorks.step3.title') }}</h4>
              <p class="text-xl sm:text-2xl text-gray-500 font-medium leading-relaxed max-w-xl">{{ $t('uTable.howItWorks.step3.description') }}</p>
            </div>
          </div>

          <!-- Step 4 -->
          <div class="flex flex-col lg:flex-row-reverse items-center gap-12 lg:gap-20">
            <div class="flex-shrink-0 w-full lg:w-1/2 max-w-lg">
              <div class="relative">
                <div class="absolute -top-6 -left-6 w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center text-white font-black text-2xl z-10 shadow-2xl">
                  4
                </div>
                <div class="bg-white border-2 border-gray-100 rounded-[40px] overflow-hidden shadow-2xl p-6">
                  <img 
                    src="https://i.ibb.co/WWkBRkwZ/Screenshot-2025-12-22-at-16-06-24.png" 
                    alt="Step 4: Gestisci la prenotazione" 
                    class="w-full h-auto rounded-3xl"
                  />
                </div>
              </div>
            </div>
            <div class="flex-1 space-y-6 text-center lg:text-right lg:pr-8">
              <h4 class="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 leading-tight">{{ $t('uTable.howItWorks.step4.title') }}</h4>
              <p class="text-xl sm:text-2xl text-gray-500 font-medium leading-relaxed max-w-xl ml-auto">{{ $t('uTable.howItWorks.step4.description') }}</p>
            </div>
          </div>
        </div>

        <div class="text-center pt-8">
          <button 
            @click="openWaitlist"
            class="px-10 py-5 bg-indigo-600 text-white rounded-[28px] font-black text-sm uppercase tracking-widest shadow-2xl shadow-indigo-200 hover:bg-indigo-700 transition-all hover:scale-105 active:scale-95 inline-flex items-center gap-2"
          >
            {{ $t('uTable.howItWorks.cta') }} <ArrowRight class="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>

    <!-- Features Grid -->
    <section class="py-40 px-8 relative bg-white border-y border-gray-100">
      <div class="max-w-7xl mx-auto space-y-24">
        <div class="max-w-2xl space-y-6">
           <h2 class="text-[11px] font-black uppercase text-indigo-600 tracking-[0.3em]">{{ $t('uTable.features.title') }}</h2>
           <h3 class="text-5xl font-black tracking-tighter text-gray-900 leading-tight">{{ $t('uTable.features.subtitle') }}</h3>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-20">
          <div 
            v-for="(feature, i) in features" 
            :key="i" 
            class="space-y-6 group cursor-pointer" 
            @click="openWaitlist"
          >
            <div class="w-16 h-16 bg-gray-50 rounded-3xl flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-6">
              <component :is="feature.icon" class="w-7 h-7" />
            </div>
            <h4 class="text-2xl font-black text-gray-900 tracking-tight">{{ $t(feature.titleKey) }}</h4>
            <p class="text-gray-500 font-medium leading-relaxed">{{ $t(feature.descKey) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ChatGPT Integration Section -->
    <section class="py-40 px-4 sm:px-8 relative overflow-hidden bg-white">
      <div class="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center relative z-10">
        <div class="space-y-10 animate-in fade-in slide-in-from-left-10 duration-1000">
          <div class="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-50 border border-indigo-100 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-[0.2em]">
            <Cpu class="w-4 h-4 fill-indigo-600/20" /> {{ $t('uTable.chatgpt.badge') }}
          </div>
          
          <h2 class="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter text-gray-900 leading-[0.9]">
            {{ $t('uTable.chatgpt.title') }}<br/> {{ $t('uTable.chatgpt.titleHighlight') }} <span class="text-indigo-600">{{ $t('uTable.chatgpt.titleHighlight2') }}</span>
          </h2>
          
          <p class="text-lg sm:text-xl text-gray-500 font-medium leading-relaxed max-w-lg">
            {{ $t('uTable.chatgpt.description') }}
          </p>
          
          <div class="flex flex-wrap items-center gap-4">
            <div class="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-100 text-amber-700 rounded-full text-[10px] font-black uppercase tracking-[0.2em]">
              <Sparkles class="w-3.5 h-3.5 fill-amber-600" /> {{ $t('uTable.chatgpt.comingSoon') }}
            </div>

            <button 
              @click="openWaitlist"
              class="px-10 py-5 bg-gray-900 text-white rounded-[24px] font-black text-[11px] uppercase tracking-widest shadow-2xl hover:bg-black transition-all hover:scale-105 active:scale-95"
            >
              {{ $t('uTable.chatgpt.cta') }}
            </button>
          </div>
        </div>
        <div class="relative flex justify-center lg:justify-end">
          <!-- ChatGPT Mockup UI -->
          <div class="w-full max-w-[440px] aspect-[4/5] bg-white rounded-[12px] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.1)] border border-gray-100 overflow-hidden flex flex-col">
            <!-- ChatGPT Header -->
            <div class="h-14 flex items-center justify-between px-6 border-b border-gray-50/50 shrink-0">
               <div class="flex items-center gap-2">
                  <Menu class="w-5 h-5 text-gray-700" />
                  <div class="flex items-center gap-1">
                     <span class="text-base font-bold text-gray-900 tracking-tight">ChatGPT</span>
                     <span class="text-base font-bold text-gray-400">5</span>
                     <ChevronRight class="w-4 h-4 text-gray-400" />
                  </div>
               </div>
               <div class="flex gap-4">
                  <Edit2 class="w-4 h-4 text-gray-700" />
                  <MoreHorizontal class="w-4 h-4 text-gray-700" />
               </div>
            </div>
            <!-- Chat Feed -->
            <div class="flex-1 overflow-y-auto p-6 space-y-6 bg-white custom-scrollbar">
              <!-- Tool Execution History Item -->
              <div class="flex justify-end pr-4 mt-2">
                 <div class="flex items-center gap-2 text-gray-400">
                    <CornerDownRight class="w-3.5 h-3.5" />
                    <span class="text-sm font-medium">{{ $t('uTable.chatgpt.usedPlugin') }}</span>
                 </div>
              </div>
              <!-- User Message Bubble -->
              <div class="self-end max-w-[85%] ml-auto">
                 <div class="bg-[#f4f4f4] p-4 px-5 rounded-[22px] rounded-tr-[4px]">
                    <p class="text-[15px] font-medium text-gray-800">
                      {{ $t('uTable.chatgpt.userMessage') }}
                    </p>
                 </div>
              </div>
              <!-- System/AI Response Intro -->
              <div class="space-y-4 pt-4">
                 <div class="flex items-center gap-2 text-gray-400">
                    <span class="text-[15px] font-medium">{{ $t('uTable.chatgpt.askedPlugin') }}</span>
                 </div>
                 <p class="text-[15px] font-medium text-gray-900 leading-tight">
                  {{ $t('uTable.chatgpt.aiResponse') }}
                 </p>
              </div>
              <!-- Source Branding Header -->
              <div class="flex items-center gap-2 pt-2">
                 <div class="w-7 h-7 bg-white rounded-full flex items-center justify-center p-1 shrink-0 border border-gray-200">
                    <img :src="LOGO_URL" class="w-full h-full object-contain" alt="U-Table" />
                 </div>
                 <span class="text-[15px] font-medium text-gray-500">U-Table</span>
              </div>
              <!-- The Main Structured Card -->
              <div class="border border-gray-100 rounded-[16px] overflow-hidden bg-white shadow-sm cursor-pointer" @click="openWaitlist">
                 <!-- Main Hero Part -->
                 <div class="p-4 flex items-center gap-4">
                    <img 
                      src="https://images.unsplash.com/photo-1544148103-0773bf10d330?auto=format&fit=crop&q=80&w=200" 
                      class="w-[80px] h-[80px] rounded-[4px] object-cover shadow-sm shrink-0" 
                      alt="Grotto" 
                    />
                    <div class="flex-1 min-w-0">
                       <h4 class="text-[17px] font-bold text-gray-900 tracking-tight truncate">{{ $t('uTable.chatgpt.bookingTitle') }}</h4>
                       <p class="text-[14px] font-medium text-gray-400 mt-0.5 truncate">{{ $t('uTable.chatgpt.bookingSubtitle') }}</p>
                    </div>
                    <div class="w-8 h-8 rounded-full border border-gray-100 flex items-center justify-center text-gray-900 shrink-0">
                       <Plus class="w-4 h-4" />
                    </div>
                 </div>
                 <!-- Divider line -->
                 <div class="h-px bg-gray-50 mx-4"></div>
                 <!-- List Item Sub-Part -->
                 <div class="px-4 py-4 space-y-4">
                    <div class="flex items-center gap-4">
                       <div class="w-[45px] h-[45px] rounded-[4px] bg-indigo-50 flex items-center justify-center shrink-0 border border-indigo-100/30 overflow-hidden">
                          <img 
                            src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&q=80&w=100" 
                            class="w-full h-full object-cover" 
                            alt="table" 
                          />
                       </div>
                       <div class="flex-1 min-w-0">
                          <p class="text-[14px] font-bold text-gray-900 truncate uppercase tracking-tight">{{ $t('uTable.chatgpt.tableType') }}</p>
                          <p class="text-[13px] font-medium text-gray-400">{{ $t('uTable.chatgpt.tableDetails') }}</p>
                       </div>
                       <span class="text-[13px] font-bold text-gray-500 whitespace-nowrap">19:30</span>
                    </div>
                 </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Voice AI Agent Hero Section -->
    <section class="py-40 px-4 sm:px-8 relative overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-lime-50 border-y border-indigo-100">
      <div class="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center relative z-10">
        <div class="space-y-10 animate-in fade-in slide-in-from-left-10 duration-1000">
          <div class="inline-flex items-center gap-2 px-4 py-1.5 bg-lime-100 border border-lime-200 text-lime-700 rounded-full text-[10px] font-black uppercase tracking-[0.2em]">
            <Mic class="w-4 h-4 fill-lime-600" /> {{ $t('uTable.voiceAi.badge') }}
          </div>
          
          <h2 class="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter text-gray-900 leading-[0.9]">
            {{ $t('uTable.voiceAi.title') }}<br/> <span class="text-indigo-600">{{ $t('uTable.voiceAi.titleHighlight') }}</span>
          </h2>
          
          <p class="text-lg sm:text-xl text-gray-500 font-medium leading-relaxed max-w-lg">
            {{ $t('uTable.voiceAi.description') }}
          </p>
          
          <div class="flex flex-wrap items-center gap-4">
            <button 
              @click="openWaitlist"
              class="px-10 py-5 bg-indigo-600 text-white rounded-[24px] font-black text-[11px] uppercase tracking-widest shadow-2xl hover:bg-indigo-700 transition-all hover:scale-105 active:scale-95 flex items-center gap-2"
            >
              {{ $t('uTable.voiceAi.cta') }} <ArrowRight class="w-4 h-4" />
            </button>
          </div>
        </div>
        <div class="relative flex justify-center lg:justify-end">
          <!-- Voice AI Mockup -->
          <div class="w-full max-w-[440px] aspect-[4/5] bg-white rounded-[12px] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.1)] border border-gray-100 overflow-hidden flex flex-col">
            <!-- Phone Header -->
            <div class="h-14 flex items-center justify-between px-6 border-b border-gray-50/50 shrink-0 bg-gradient-to-r from-indigo-600 to-indigo-700">
               <div class="flex items-center gap-2">
                  <Phone class="w-5 h-5 text-white" />
                  <span class="text-base font-bold text-white tracking-tight">{{ $t('uTable.voiceAi.phoneHeader') }}</span>
               </div>
               <div class="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            </div>
            <!-- Call Interface -->
            <div class="flex-1 overflow-y-auto p-6 space-y-6 bg-gradient-to-b from-gray-50 to-white custom-scrollbar">
              <div class="text-center space-y-4 pt-8">
                <div class="w-24 h-24 bg-indigo-100 rounded-full flex items-center justify-center mx-auto">
                  <Mic class="w-12 h-12 text-indigo-600" />
                </div>
                <div>
                  <p class="text-lg font-bold text-gray-900">{{ $t('uTable.voiceAi.callStatus') }}</p>
                  <p class="text-sm text-gray-500 mt-1">{{ $t('uTable.voiceAi.callSubtext') }}</p>
                </div>
              </div>
              <div class="space-y-3 pt-4">
                <div class="bg-white border border-gray-100 rounded-[16px] p-4 shadow-sm">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Check class="w-5 h-5 text-green-600" />
                    </div>
                    <div class="flex-1">
                      <p class="text-sm font-bold text-gray-900">{{ $t('uTable.voiceAi.bookingConfirmed') }}</p>
                      <p class="text-xs text-gray-500">{{ $t('uTable.voiceAi.bookingDetails') }}</p>
                    </div>
                  </div>
                </div>
                <div class="bg-indigo-50 border border-indigo-100 rounded-[16px] p-4">
                  <div class="flex items-center gap-3">
                    <Bell class="w-5 h-5 text-indigo-600" />
                    <p class="text-sm font-bold text-indigo-900">{{ $t('uTable.voiceAi.notificationSent') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Voice AI Features Section -->
    <section class="py-40 px-8 relative bg-white">
      <div class="max-w-7xl mx-auto space-y-24">
        <div class="text-center space-y-6 max-w-3xl mx-auto">
          <h2 class="text-[11px] font-black uppercase text-indigo-600 tracking-[0.3em]">{{ $t('uTable.voiceAi.features.title') }}</h2>
          <h3 class="text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-gray-900 leading-tight">{{ $t('uTable.voiceAi.features.subtitle') }}</h3>
          <p class="text-gray-500 font-medium text-lg">{{ $t('uTable.voiceAi.features.description') }}</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div 
            v-for="(feature, i) in voiceAiFeatures" 
            :key="i" 
            class="bg-white border-2 border-gray-100 rounded-[32px] p-8 space-y-6 hover:border-indigo-200 hover:shadow-xl transition-all duration-300 group cursor-pointer"
            @click="openWaitlist"
          >
            <div class="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-300">
              <component :is="feature.icon" class="w-8 h-8 text-indigo-600 group-hover:text-white transition-colors" />
            </div>
            <div>
              <h4 class="text-xl font-black text-gray-900 mb-2">{{ $t(feature.titleKey) }}</h4>
              <p class="text-sm text-gray-500 leading-relaxed">{{ $t(feature.descKey) }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Voice AI Benefits Section -->
    <section class="py-40 px-4 sm:px-8 relative bg-gradient-to-br from-gray-900 via-indigo-900 to-gray-900 text-white overflow-hidden">
      <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-600/20 rounded-full blur-[120px] pointer-events-none"></div>
      
      <div class="max-w-7xl mx-auto space-y-24 relative z-10">
        <div class="text-center space-y-6 max-w-3xl mx-auto">
          <h2 class="text-[11px] font-black uppercase text-indigo-400 tracking-[0.3em]">{{ $t('uTable.voiceAi.benefits.title') }}</h2>
          <h3 class="text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-white leading-tight">{{ $t('uTable.voiceAi.benefits.subtitle') }}</h3>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div 
            v-for="(benefit, i) in voiceAiBenefits" 
            :key="i" 
            class="bg-white/5 border border-white/10 rounded-[32px] p-8 space-y-4 backdrop-blur-sm hover:bg-white/10 transition-all"
          >
            <div class="flex items-center gap-4">
              <div class="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                <component :is="benefit.icon" class="w-6 h-6 text-white" />
              </div>
              <h4 class="text-xl font-black text-white">{{ $t(benefit.titleKey) }}</h4>
            </div>
            <p class="text-gray-300 font-medium leading-relaxed">{{ $t(benefit.descKey) }}</p>
          </div>
        </div>

        <div class="text-center pt-8">
          <button 
            @click="openWaitlist"
            class="px-10 py-5 bg-[#DFFF1A] text-black rounded-[28px] font-black text-sm uppercase tracking-widest shadow-2xl shadow-lime-200/50 hover:bg-lime-400 transition-all hover:scale-105 active:scale-95 inline-flex items-center gap-2"
          >
            {{ $t('uTable.voiceAi.benefits.cta') }} <ArrowRight class="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>

    <!-- Integration Section -->
    <section class="py-40 px-4 sm:px-8 relative bg-white">
      <div class="max-w-6xl mx-auto space-y-16">
        <div class="text-center space-y-6 max-w-3xl mx-auto">
          <h2 class="text-[11px] font-black uppercase text-indigo-600 tracking-[0.3em]">{{ $t('uTable.integration.title') }}</h2>
          <h3 class="text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-gray-900 leading-tight">{{ $t('uTable.integration.subtitle') }}</h3>
          <p class="text-gray-500 font-medium text-lg">{{ $t('uTable.integration.description') }}</p>
        </div>

        <div class="max-w-2xl mx-auto">
          <div class="bg-white border-2 border-indigo-100 rounded-[32px] p-8 sm:p-10 shadow-xl">
            <div class="space-y-6">
              <div class="flex items-start gap-4">
                <div class="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Code class="w-7 h-7 text-indigo-600" />
                </div>
                <div>
                  <h4 class="text-xl font-black text-gray-900 mb-1">{{ $t('uTable.integration.iframeTitle') }}</h4>
                  <p class="text-sm text-gray-500">{{ $t('uTable.integration.iframeDesc') }}</p>
                </div>
              </div>
              <div class="flex items-start gap-4">
                <div class="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Globe class="w-7 h-7 text-indigo-600" />
                </div>
                <div>
                  <h4 class="text-xl font-black text-gray-900 mb-1">{{ $t('uTable.integration.slugTitle') }}</h4>
                  <p class="text-sm text-gray-500">{{ $t('uTable.integration.slugDesc') }}</p>
                </div>
              </div>
              <div class="flex items-start gap-4">
                <div class="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Settings2 class="w-7 h-7 text-indigo-600" />
                </div>
                <div>
                  <h4 class="text-xl font-black text-gray-900 mb-1">{{ $t('uTable.integration.zeroConfigTitle') }}</h4>
                  <p class="text-sm text-gray-500">{{ $t('uTable.integration.zeroConfigDesc') }}</p>
                </div>
              </div>
            </div>
          </div>
          <button 
            @click="openWaitlist"
            class="w-full mt-8 px-8 py-5 bg-indigo-600 text-white rounded-[28px] font-black text-sm uppercase tracking-widest shadow-2xl shadow-indigo-200 hover:bg-indigo-700 transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
          >
            {{ $t('uTable.integration.cta') }} <ArrowRight class="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>

    <!-- Comparison Section -->
    <section class="py-40 px-4 sm:px-8 relative overflow-hidden bg-white">
      <div class="max-w-7xl mx-auto space-y-24">
        <div class="text-center space-y-6 max-w-3xl mx-auto">
          <h2 class="text-[11px] font-black uppercase text-indigo-600 tracking-[0.3em]">{{ $t('uTable.comparison.title') }}</h2>
          <h3 class="text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-gray-900 leading-tight">{{ $t('uTable.comparison.subtitle') }}</h3>
          <p class="text-gray-500 font-medium text-base sm:text-lg">{{ $t('uTable.comparison.description') }}</p>
        </div>

        <div class="stripe-card border border-gray-100 overflow-hidden shadow-2xl rounded-[40px] overflow-x-auto">
          <table class="w-full border-collapse min-w-[600px]">
            <thead>
              <tr class="bg-gray-900 text-white">
                <th class="p-6 sm:p-8 text-left text-xs font-black uppercase tracking-widest">{{ $t('uTable.comparison.feature') }}</th>
                <th class="p-6 sm:p-8 text-center text-xs font-black uppercase tracking-widest bg-indigo-600">U-Table</th>
                <th class="p-6 sm:p-8 text-center text-xs font-black uppercase tracking-widest opacity-60">The Fork</th>
                <th class="p-6 sm:p-8 text-center text-xs font-black uppercase tracking-widest opacity-60">Localsearch</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-50">
              <tr>
                <td class="p-6 sm:p-8 font-black text-gray-900">{{ $t('uTable.comparison.commissionPerCover') }}</td>
                <td class="p-6 sm:p-8 text-center bg-indigo-50/50 font-black text-indigo-600">{{ formatPrice('0.00') }}</td>
                <td class="p-6 sm:p-8 text-center text-gray-400 font-bold">{{ formatPrice('2.50') }} - {{ formatPrice('3.50') }}</td>
                <td class="p-6 sm:p-8 text-center text-gray-400 font-bold">{{ $t('uTable.comparison.variableHigh') }}</td>
              </tr>
              <tr>
                <td class="p-6 sm:p-8 font-black text-gray-900">{{ $t('uTable.comparison.smsReminder') }}</td>
                <td class="p-6 sm:p-8 text-center bg-indigo-50/50">
                  <div class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mx-auto text-white">
                    <Check class="w-5 h-5" />
                  </div>
                </td>
                <td class="p-6 sm:p-8 text-center text-gray-300 font-bold">{{ $t('uTable.comparison.proOnly') }}</td>
                <td class="p-6 sm:p-8 text-center text-gray-300 font-bold">{{ $t('uTable.comparison.absent') }}</td>
              </tr>
              <tr>
                <td class="p-6 sm:p-8 font-black text-gray-900">{{ $t('uTable.comparison.customSlug') }}</td>
                <td class="p-6 sm:p-8 text-center bg-indigo-50/50">
                  <div class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mx-auto text-white">
                    <Check class="w-5 h-5" />
                  </div>
                </td>
                <td class="p-6 sm:p-8 text-center text-gray-300 font-bold">{{ $t('uTable.comparison.no') }}</td>
                <td class="p-6 sm:p-8 text-center text-gray-300 font-bold">{{ $t('uTable.comparison.absent') }}</td>
              </tr>
              <tr>
                <td class="p-6 sm:p-8 font-black text-gray-900">{{ $t('uTable.comparison.modifyReservation') }}</td>
                <td class="p-6 sm:p-8 text-center bg-indigo-50/50">
                  <div class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mx-auto text-white">
                    <Check class="w-5 h-5" />
                  </div>
                </td>
                <td class="p-6 sm:p-8 text-center text-gray-300 font-bold">{{ $t('uTable.comparison.yes') }}</td>
                <td class="p-6 sm:p-8 text-center text-gray-300 font-bold">{{ $t('uTable.comparison.no') }}</td>
              </tr>
              <tr class="bg-indigo-600/5">
                <td class="p-6 sm:p-8 font-black text-indigo-600">{{ $t('uTable.comparison.setupCost') }}</td>
                <td class="p-6 sm:p-8 text-center font-black text-indigo-600">{{ $t('uTable.comparison.free') }}</td>
                <td class="p-6 sm:p-8 text-center text-gray-400 font-bold">{{ $t('uTable.comparison.variable') }}</td>
                <td class="p-6 sm:p-8 text-center text-gray-400 font-bold">{{ formatPrice('300') }}+</td>
              </tr>
              <tr>
                <td class="p-6 sm:p-8 font-black text-gray-900">{{ $t('uTable.comparison.monthlyPrice') }}</td>
                <td class="p-6 sm:p-8 text-center bg-indigo-50/50 font-black text-indigo-600">{{ formatPrice('0') }} - {{ formatPrice('200') }}</td>
                <td class="p-6 sm:p-8 text-center text-gray-400 font-bold">{{ formatPrice('79') }} - {{ formatPrice('149') }}</td>
                <td class="p-6 sm:p-8 text-center text-gray-400 font-bold">{{ formatPrice('99') }} - {{ formatPrice('199') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10 pt-10">
          <div class="flex items-center gap-4 p-6 sm:p-8 bg-indigo-50 rounded-[32px] border border-indigo-100">
            <BarChart3 class="w-10 h-10 sm:w-12 sm:h-12 text-indigo-600 shrink-0" />
            <div>
              <p class="text-xl sm:text-2xl font-black text-gray-900">{{ $t('uTable.comparison.savings') }}</p>
              <p class="text-xs sm:text-sm font-bold text-indigo-600 uppercase tracking-widest">{{ formatPrice('4200') }} / {{ $t('uTable.comparison.perYear') }}</p>
            </div>
          </div>
          <button 
            @click="openWaitlist"
            class="px-10 sm:px-12 py-5 sm:py-6 bg-gray-900 text-white rounded-[24px] font-black text-xs sm:text-sm uppercase tracking-widest shadow-2xl hover:bg-black transition-all hover:scale-105 active:scale-95"
          >
            {{ $t('uTable.comparison.cta') }}
          </button>
        </div>
      </div>
    </section>

    <!-- Pricing / Free Section -->
    <section class="py-40 px-8 bg-gray-900 text-white overflow-hidden relative">
      <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-600/20 rounded-full blur-[120px] pointer-events-none"></div>
      
      <div class="max-w-5xl mx-auto text-center space-y-16 relative z-10">
        <div class="space-y-6">
           <h2 class="text-[11px] font-black uppercase text-indigo-400 tracking-[0.3em]">{{ $t('uTable.pricing.title') }}</h2>
           <h3 class="text-6xl font-black tracking-tighter text-white">{{ $t('uTable.pricing.subtitle') }}</h3>
           <p class="text-xl text-gray-400 font-medium max-w-2xl mx-auto">{{ $t('uTable.pricing.description') }}</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
          <div class="bg-white/5 border border-white/10 p-12 rounded-[40px] space-y-10">
             <div class="space-y-2">
               <h4 class="text-2xl font-black">{{ $t('uTable.pricing.earlyBird') }}</h4>
               <p class="text-5xl font-black">{{ formatPrice('0') }} <span class="text-xl text-white/40">{{ $t('uTable.pricing.perMonth') }}</span></p>
             </div>
             <ul class="space-y-4">
               <li 
                 v-for="item in earlyBirdFeatures" 
                 :key="item" 
                 class="flex items-center gap-3 text-sm font-bold text-white/70"
               >
                 <Check class="w-4 h-4 text-[#DFFF1A]" /> {{ $t(item) }}
               </li>
             </ul>
             <div class="pt-4 border-t border-white/10">
               <p class="text-xs font-bold text-white/50 mb-2">{{ $t('uTable.pricing.voiceAiAddon') }}</p>
               <p class="text-sm font-bold text-white/70">{{ $t('uTable.pricing.voiceAiEarlyBird') }}</p>
             </div>
             <button 
              @click="openWaitlist"
              class="w-full py-5 bg-white text-black rounded-3xl font-black text-xs uppercase tracking-widest hover:bg-[#DFFF1A] transition-all"
             >
               {{ $t('uTable.pricing.ctaEarly') }}
             </button>
          </div>
          <div class="bg-indigo-600 p-12 rounded-[40px] space-y-10 shadow-2xl shadow-indigo-500/20">
             <div class="space-y-2">
               <h4 class="text-2xl font-black">{{ $t('uTable.pricing.professional') }}</h4>
               <p class="text-5xl font-black">{{ formatPrice('49') }} <span class="text-xl text-white/60">{{ $t('uTable.pricing.perMonth') }}</span></p>
             </div>
             <ul class="space-y-4">
               <li 
                 v-for="item in professionalFeatures" 
                 :key="item" 
                 class="flex items-center gap-3 text-sm font-bold text-white/90"
               >
                 <Check class="w-4 h-4 text-[#DFFF1A]" /> {{ $t(item) }}
               </li>
             </ul>
             <div class="pt-4 border-t border-white/20">
               <p class="text-xs font-bold text-white/60 mb-2">{{ $t('uTable.pricing.voiceAiAddon') }}</p>
               <p class="text-sm font-bold text-white/90">{{ $t('uTable.pricing.voiceAiProfessional') }}</p>
             </div>
             <button 
              @click="openWaitlist"
              class="w-full py-5 bg-gray-900 text-white rounded-3xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all"
             >
               {{ $t('uTable.pricing.ctaPro') }}
             </button>
          </div>
          <div class="bg-gradient-to-br from-lime-500/20 to-[#DFFF1A]/20 border-2 border-lime-400/50 p-12 rounded-[40px] space-y-10 shadow-2xl shadow-lime-500/20 relative">
             <div class="absolute top-4 right-4 bg-lime-400 text-gray-900 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest">
               {{ $t('uTable.pricing.popular') }}
             </div>
             <div class="space-y-2">
               <h4 class="text-2xl font-black">{{ $t('uTable.pricing.aiPremium') }}</h4>
               <p class="text-5xl font-black">{{ formatPrice('200') }} <span class="text-xl text-white/60">{{ $t('uTable.pricing.perMonth') }}</span></p>
             </div>
             <ul class="space-y-4">
               <li 
                 v-for="item in aiPremiumFeatures" 
                 :key="item" 
                 class="flex items-center gap-3 text-sm font-bold text-white/90"
               >
                 <Check class="w-4 h-4 text-[#DFFF1A]" /> {{ $t(item) }}
               </li>
             </ul>
             <button 
              @click="openWaitlist"
              class="w-full py-5 bg-[#DFFF1A] text-black rounded-3xl font-black text-xs uppercase tracking-widest hover:bg-lime-400 transition-all shadow-xl shadow-lime-500/30"
             >
               {{ $t('uTable.pricing.ctaPremium') }}
             </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Waitlist Promotion Section -->
    <section class="py-32 px-4 sm:px-8 bg-gradient-to-br from-indigo-50 via-white to-indigo-50 border-y border-indigo-100">
      <div class="max-w-4xl mx-auto">
        <div class="bg-white rounded-[40px] p-8 sm:p-12 shadow-2xl shadow-indigo-100/50 border border-indigo-100">
          <div class="text-center space-y-8">
            <div class="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-100 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-[0.2em]">
              <Sparkles class="w-3.5 h-3.5 fill-indigo-600" /> {{ $t('uTable.waitlist.limitedOffer') }}
            </div>
            <div class="space-y-4">
              <h3 class="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-gray-900 leading-tight">
                {{ $t('uTable.waitlist.title') }} <span class="text-indigo-600">{{ $t('uTable.waitlist.highlight') }}</span> {{ $t('uTable.waitlist.subtitle') }} <span class="text-indigo-600">{{ $t('uTable.waitlist.subtitleHighlight') }}</span>
              </h3>
              <p class="text-lg sm:text-xl text-gray-600 font-medium max-w-2xl mx-auto leading-relaxed">
                {{ $t('uTable.waitlist.description') }}
              </p>
            </div>
            <div class="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <button 
                @click="openWaitlist"
                class="px-10 py-5 bg-[#DFFF1A] text-black border border-[#DFFF1A] rounded-[28px] font-black text-xs uppercase tracking-widest shadow-2xl shadow-lime-200/50 hover:bg-lime-400 transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
              >
                {{ $t('uTable.waitlist.cta') }} <ArrowRight class="w-4 h-4" />
              </button>
              <p class="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{{ $t('uTable.waitlist.disclaimer') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="py-40 px-8">
      <div class="max-w-3xl mx-auto space-y-20 text-center">
        <div class="pt-20 space-y-10">
           <div class="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mx-auto text-indigo-600">
              <Zap class="w-10 h-10 fill-indigo-600" />
           </div>
           <h3 class="text-5xl font-black tracking-tighter text-gray-900">{{ $t('uTable.finalCta.title') }} <span class="text-indigo-600">{{ $t('uTable.finalCta.titleHighlight') }}</span></h3>
           <button 
              @click="openWaitlist"
              class="px-12 py-6 bg-indigo-600 text-white rounded-[32px] font-black text-sm uppercase tracking-[0.2em] shadow-2xl shadow-indigo-200 hover:bg-indigo-700 hover:scale-105 transition-all"
           >
              {{ $t('uTable.finalCta.cta') }}
           </button>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="py-20 px-8 border-t border-gray-100 bg-white z-10 relative hidden">
      <div class="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-10">
        <div class="flex items-center gap-3">
           <img :src="LOGO_URL" class="w-8 h-8 object-contain opacity-50" alt="logo" />
           <span class="text-gray-400 font-black tracking-tighter">U-Table</span>
        </div>
        <div class="flex gap-10 text-[10px] font-black uppercase text-gray-400 tracking-widest">
           <button @click="openWaitlist" class="hover:text-indigo-600 cursor-pointer">{{ $t('uTable.footer.privacy') }}</button>
           <button @click="openWaitlist" class="hover:text-indigo-600 cursor-pointer">{{ $t('uTable.footer.terms') }}</button>
           <button @click="openWaitlist" class="hover:text-indigo-600 cursor-pointer">{{ $t('uTable.footer.support') }}</button>
        </div>
        <p class="text-[10px] font-black uppercase text-gray-300 tracking-widest">© 2024 Made with ❤️ Worldwide</p>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import LanguageSwitcher from '../components/LanguageSwitcher.vue'
import { 
  Zap, 
  Users, 
  ArrowRight, 
  Check, 
  Globe, 
  Code, 
  Settings2, 
  MousePointerClick,
  Mail,
  Smartphone,
  Sparkles,
  CheckCircle2,
  X,
  Lock,
  BarChart3,
  Cpu,
  Menu,
  ChevronRight,
  Edit2,
  MoreHorizontal,
  CornerDownRight,
  Plus,
  Phone,
  Mic,
  MessageCircle,
  Clock,
  Languages,
  Bell,
  TrendingDown
} from 'lucide-vue-next'

defineOptions({
  name: 'UTablePage'
})

const LOGO_URL = "https://i.ibb.co/v49VCvSF/u-table-removebg-preview.png"

const isModalOpen = ref(false)
const heroEmail = ref('')
const modalEmail = ref('')
const businessName = ref('')
const submitted = ref(false)
const mobileMenuOpen = ref(false)
const isSubmitting = ref(false)
const currency = ref('$') // Default to USD
const countryCode = ref('US') // Default country code

const features = [
  { icon: Zap, titleKey: "uTable.features.workflow.title", descKey: "uTable.features.workflow.desc" },
  { icon: Smartphone, titleKey: "uTable.features.reminder.title", descKey: "uTable.features.reminder.desc" },
  { icon: Globe, titleKey: "uTable.features.slug.title", descKey: "uTable.features.slug.desc" },
  { icon: Code, titleKey: "uTable.features.iframe.title", descKey: "uTable.features.iframe.desc" },
  { icon: MousePointerClick, titleKey: "uTable.features.selfService.title", descKey: "uTable.features.selfService.desc" },
  { icon: Settings2, titleKey: "uTable.features.multiLocation.title", descKey: "uTable.features.multiLocation.desc" }
]

const earlyBirdFeatures = [
  "uTable.pricing.earlyBirdFeatures.f1",
  "uTable.pricing.earlyBirdFeatures.f2",
  "uTable.pricing.earlyBirdFeatures.f3",
  "uTable.pricing.earlyBirdFeatures.f4"
]

const professionalFeatures = [
  "uTable.pricing.professionalFeatures.f1",
  "uTable.pricing.professionalFeatures.f2",
  "uTable.pricing.professionalFeatures.f3",
  "uTable.pricing.professionalFeatures.f4",
  "uTable.pricing.professionalFeatures.f5"
]

const aiPremiumFeatures = [
  "uTable.pricing.aiPremiumFeatures.f1",
  "uTable.pricing.aiPremiumFeatures.f2",
  "uTable.pricing.aiPremiumFeatures.f3",
  "uTable.pricing.aiPremiumFeatures.f4",
  "uTable.pricing.aiPremiumFeatures.f5",
  "uTable.pricing.aiPremiumFeatures.f6",
  "uTable.pricing.aiPremiumFeatures.f7"
]

const voiceAiFeatures = [
  {
    icon: Clock,
    titleKey: "uTable.voiceAi.features.availability.title",
    descKey: "uTable.voiceAi.features.availability.desc"
  },
  {
    icon: Phone,
    titleKey: "uTable.voiceAi.features.automatic.title",
    descKey: "uTable.voiceAi.features.automatic.desc"
  },
  {
    icon: Languages,
    titleKey: "uTable.voiceAi.features.multilanguage.title",
    descKey: "uTable.voiceAi.features.multilanguage.desc"
  },
  {
    icon: MessageCircle,
    titleKey: "uTable.voiceAi.features.notifications.title",
    descKey: "uTable.voiceAi.features.notifications.desc"
  },
  {
    icon: TrendingDown,
    titleKey: "uTable.voiceAi.features.noShow.title",
    descKey: "uTable.voiceAi.features.noShow.desc"
  },
  {
    icon: BarChart3,
    titleKey: "uTable.voiceAi.features.transcripts.title",
    descKey: "uTable.voiceAi.features.transcripts.desc"
  }
]

const voiceAiBenefits = [
  {
    icon: Clock,
    titleKey: "uTable.voiceAi.benefits.time.title",
    descKey: "uTable.voiceAi.benefits.time.desc"
  },
  {
    icon: TrendingDown,
    titleKey: "uTable.voiceAi.benefits.cost.title",
    descKey: "uTable.voiceAi.benefits.cost.desc"
  },
  {
    icon: Zap,
    titleKey: "uTable.voiceAi.benefits.efficiency.title",
    descKey: "uTable.voiceAi.benefits.efficiency.desc"
  },
  {
    icon: Users,
    titleKey: "uTable.voiceAi.benefits.customer.title",
    descKey: "uTable.voiceAi.benefits.customer.desc"
  }
]

const openWaitlist = () => {
  modalEmail.value = heroEmail.value
  isModalOpen.value = true
  mobileMenuOpen.value = false
}

const handleMobileWaitlist = () => {
  mobileMenuOpen.value = false
  // Small delay to ensure menu closes before modal opens
  setTimeout(() => {
    openWaitlist()
  }, 100)
}

const closeModal = () => {
  isModalOpen.value = false
  submitted.value = false
}

const handleHeroSubmit = (e) => {
  e.preventDefault()
  openWaitlist()
}

// Detect user's country and set currency
const detectCurrency = async () => {
  try {
    // Try to get country from IP geolocation
    const response = await fetch('https://ipapi.co/json/')
    const data = await response.json()
    const country = data.country_code || 'US'
    countryCode.value = country
    
    // Set currency based on country
    if (country === 'CH') {
      currency.value = 'CHF'
    } else if (country === 'IT') {
      currency.value = '€'
    } else if (country === 'US') {
      currency.value = '$'
    } else {
      // Default to USD for other countries
      currency.value = '$'
    }
  } catch (error) {
    console.error('Error detecting currency:', error)
    // Default to USD on error
    currency.value = '$'
    countryCode.value = 'US'
  }
}

// Format price with currency
const formatPrice = (amount) => {
  const numAmount = parseFloat(amount)
  if (currency.value === '€') {
    return `€${numAmount.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  } else if (currency.value === 'CHF') {
    return `CHF ${numAmount.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  } else {
    return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
}

// Call on mount
onMounted(() => {
  detectCurrency()
})

const handleModalSubmit = async (e) => {
  e.preventDefault()
  if (!modalEmail.value || isSubmitting.value) return
  
  try {
    isSubmitting.value = true
    
    // Prepare form data for Panettone Analytics
    const formData = {
      tracking_code: 'AVElLWEEFS1qC3341qy6mQ',
      url: window.location.href,
      form_name: 'U-Table Waitlist',
      form_id: 'u-table-waitlist',
      form_action: window.location.href,
      form_data: {
        email: modalEmail.value,
        businessName: businessName.value || 'N/A',
        source: 'u-table-landing',
        timestamp: new Date().toISOString(),
        currency: currency.value,
        countryCode: countryCode.value
      },
      field_names: ['email', 'businessName'],
      referrer: document.referrer || null,
      user_agent: navigator.userAgent || null
    }

    // Submit to Panettone Analytics API
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        'https://panettone-zeta.vercel.app/analytics/api/c',
        new Blob([JSON.stringify(formData)], { type: 'application/json' })
      )
      submitted.value = true
      // Reset form after success
      setTimeout(() => {
        modalEmail.value = ''
        businessName.value = ''
      }, 100)
    } else {
      const response = await fetch('https://panettone-zeta.vercel.app/analytics/api/c', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        keepalive: true
      })

      if (response.ok) {
        submitted.value = true
        // Reset form after success
        setTimeout(() => {
          modalEmail.value = ''
          businessName.value = ''
        }, 100)
      } else {
        throw new Error('Form submission failed')
      }
    }
  } catch (error) {
    console.error('Form submission error:', error)
    // Still show success to user even if submission fails (graceful degradation)
    submitted.value = true
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
/* Base styles */
body {
  font-family: 'Inter', sans-serif;
  background-color: #fcfcfc;
  color: #1a1a1a;
}

.stripe-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03);
}

.stripe-input {
  background-color: white !important;
  border: 1px solid #e6e6e6;
  transition: all 0.2s;
}

.stripe-input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
  outline: none;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #6366f1;
  border-radius: 10px;
}

select.stripe-input {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1rem;
  padding-right: 2.5rem;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slide-in-from-left-10 {
  from {
    transform: translateX(-2.5rem);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes zoom-in-95 {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes bounce-slow {
  0%, 100% { 
    transform: translateY(-5%); 
  }
  50% { 
    transform: translateY(0); 
  }
}

.animate-in {
  animation-fill-mode: both;
}

.fade-in {
  animation: fade-in 1s ease-out;
}

.slide-in-from-left-10 {
  animation: slide-in-from-left-10 1s ease-out;
}

.zoom-in-95 {
  animation: zoom-in-95 1s ease-out;
}

.animate-bounce-slow {
  animation: bounce-slow 4s ease-in-out infinite;
}

.duration-1000 {
  animation-duration: 1000ms;
}

.delay-200 {
  animation-delay: 200ms;
}

.duration-300 {
  animation-duration: 300ms;
}

.duration-500 {
  animation-duration: 500ms;
}
</style>
